import { motion } from "framer-motion";
import { ArrowUpRight, Eye, Star, ThumbsUp } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer } from "recharts";
import { formatNum } from "@/lib/api";

const FALLBACK = "https://images.unsplash.com/photo-1542751371-adc38448a05e?w=1200";

export const FeaturedGame = ({ game }) => {
  if (!game) return null;
  return (
    <section id="featured" className="max-w-[1600px] mx-auto px-6 md:px-10 py-16 md:py-24">
      <div className="flex items-center gap-3 mb-10">
        <span className="w-1.5 h-1.5 rounded-full bg-volt animate-pulse" />
        <span className="font-mono text-xs tracking-[0.25em] uppercase text-volt">Featured · Live</span>
      </div>
      <motion.a
        href={game.url || "#"} target="_blank" rel="noreferrer" data-testid="featured-game"
        initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
        className="group grid md:grid-cols-2 border border-border bg-card overflow-hidden hover:border-volt transition-colors duration-300"
      >
        <div className="relative min-h-[280px] md:min-h-[460px] overflow-hidden order-1 md:order-2">
          <img src={game.thumbnail_url || FALLBACK} alt={game.name} className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 ease-out" />
          <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-l from-card via-card/30 to-transparent" />
        </div>

        <div className="p-8 md:p-12 flex flex-col justify-between gap-8 order-2 md:order-1">
          <div>
            <span className="font-mono text-[10px] tracking-[0.2em] uppercase text-muted-foreground">Now playing</span>
            <div className="flex items-baseline gap-3 mt-2">
              <span className="font-display font-black text-6xl md:text-8xl tracking-tighter text-volt text-glow leading-none" data-testid="featured-playing">{formatNum(game.playing)}</span>
              <span className="font-mono text-xs tracking-[0.2em] uppercase text-muted-foreground">online</span>
            </div>
            <h3 className="font-display font-extrabold text-3xl md:text-5xl tracking-tight uppercase mt-6">{game.name || "Untitled Game"}</h3>
            {game.description && <p className="text-muted-foreground mt-4 max-w-md line-clamp-3">{game.description}</p>}
          </div>

          {Array.isArray(game.history) && game.history.length >= 2 && (
            <div className="h-16 w-full min-w-0">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={game.history.map((h) => ({ v: h.playing }))} margin={{ top: 4, bottom: 0, left: 0, right: 0 }}>
                  <defs>
                    <linearGradient id="spark-featured" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#FF2E2E" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#FF2E2E" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area type="monotone" dataKey="v" stroke="#FF2E2E" strokeWidth={2} fill="url(#spark-featured)" isAnimationActive={false} dot={false} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}

          <div className="flex items-center justify-between border-t border-border pt-6">
            <div className="flex gap-8">
              <Metric icon={Eye} label="Visits" value={formatNum(game.visits)} />
              <Metric icon={Star} label="Favorites" value={formatNum(game.favorites)} />
              <Metric icon={ThumbsUp} label="Rating" value={game.like_ratio ? game.like_ratio + "%" : "—"} />
            </div>
            <span className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-volt group-hover:gap-3 transition-all duration-200">
              Play <ArrowUpRight className="w-4 h-4" />
            </span>
          </div>
        </div>
      </motion.a>
    </section>
  );
};

const Metric = ({ icon: Icon, label, value }) => (
  <div>
    <div className="flex items-center gap-1.5"><Icon className="w-3.5 h-3.5 text-volt" /><span className="font-display font-bold text-lg">{value}</span></div>
    <div className="font-mono text-[9px] tracking-[0.15em] uppercase text-muted-foreground mt-1">{label}</div>
  </div>
);
